import React, {
    createContext,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useReducer,
    useRef,
} from 'react';
import baseURL from '../url';

const CatalogDataContext = createContext(null);

const RESOURCE_CONFIG = Object.freeze({
    productos: {
        endpoint: 'productosGet.php',
        responseKey: 'productos',
        errorMessage: 'No fue posible cargar los productos.',
    },
    categorias: {
        endpoint: 'categoriasGet.php',
        responseKey: 'categorias',
        errorMessage: 'No fue posible cargar las categorías.',
    },
    subcategorias: {
        endpoint: 'subCategoriaGet.php',
        responseKey: 'subcategorias',
        errorMessage: 'No fue posible cargar las subcategorías.',
    },
    tienda: {
        endpoint: 'tiendaGet.php',
        responseKey: 'tienda',
        errorMessage: 'No fue posible cargar la información de la tienda.',
    },
});

const RESOURCE_NAMES = Object.keys(RESOURCE_CONFIG);

const createResourceState = () => ({
    data: [],
    status: 'idle',
    error: null,
    updatedAt: null,
});

const createInitialResources = () => RESOURCE_NAMES.reduce((resources, resourceName) => {
    resources[resourceName] = createResourceState();
    return resources;
}, {});

const resourcesReducer = (resources, action) => ({
    ...resources,
    [action.resourceName]: action.nextResource,
});

const assertResourceName = (resourceName) => {
    if (!RESOURCE_CONFIG[resourceName]) {
        throw new Error(`Recurso de catálogo desconocido: ${resourceName}`);
    }
};

export function CatalogDataProvider({ children }) {
    const [resources, dispatch] = useReducer(resourcesReducer, undefined, createInitialResources);
    const resourcesRef = useRef(createInitialResources());
    const inFlightRef = useRef({});
    const controllersRef = useRef({});
    const generationsRef = useRef(
        RESOURCE_NAMES.reduce((generations, resourceName) => {
            generations[resourceName] = 0;
            return generations;
        }, {})
    );
    const mountedRef = useRef(true);

    useEffect(() => {
        mountedRef.current = true;

        return () => {
            mountedRef.current = false;
        };
    }, []);

    const commitResource = useCallback((resourceName, patch) => {
        const nextResource = {
            ...resourcesRef.current[resourceName],
            ...patch,
        };

        resourcesRef.current[resourceName] = nextResource;

        if (mountedRef.current) {
            dispatch({ resourceName, nextResource });
        }

        return nextResource;
    }, []);

    const ensureResource = useCallback((resourceName) => {
        assertResourceName(resourceName);

        const currentResource = resourcesRef.current[resourceName];
        if (currentResource.status === 'loaded') {
            return Promise.resolve(currentResource.data);
        }

        if (inFlightRef.current[resourceName]) {
            return inFlightRef.current[resourceName];
        }

        const config = RESOURCE_CONFIG[resourceName];
        const requestGeneration = generationsRef.current[resourceName];
        const controller = typeof AbortController === 'function'
            ? new AbortController()
            : null;

        controllersRef.current[resourceName] = controller;
        commitResource(resourceName, {
            status: 'loading',
            error: null,
        });

        let request;
        request = (async () => {
            try {
                const requestOptions = {
                    method: 'GET',
                    ...(controller ? { signal: controller.signal } : {}),
                };
                const response = await fetch(`${baseURL}/${config.endpoint}`, requestOptions);

                if (!response?.ok) {
                    throw new Error(config.errorMessage);
                }

                const payload = await response.json();
                const receivedCollection = payload?.[config.responseKey];

                if (!Array.isArray(receivedCollection)) {
                    throw new Error(config.errorMessage);
                }

                const collection = receivedCollection.slice();
                if (resourceName === 'tienda') {
                    collection.reverse();
                }

                if (generationsRef.current[resourceName] !== requestGeneration) {
                    throw new Error('La solicitud fue invalidada.');
                }

                commitResource(resourceName, {
                    data: collection,
                    status: 'loaded',
                    error: null,
                    updatedAt: Date.now(),
                });

                return collection;
            } catch (error) {
                const requestIsCurrent = generationsRef.current[resourceName] === requestGeneration;

                if (requestIsCurrent) {
                    const publicError = new Error(config.errorMessage);
                    commitResource(resourceName, {
                        data: [],
                        status: 'error',
                        error: publicError,
                        updatedAt: null,
                    });
                    throw publicError;
                }

                throw error;
            } finally {
                if (inFlightRef.current[resourceName] === request) {
                    delete inFlightRef.current[resourceName];
                }
                if (controllersRef.current[resourceName] === controller) {
                    delete controllersRef.current[resourceName];
                }
            }
        })();

        inFlightRef.current[resourceName] = request;
        return request;
    }, [commitResource]);

    const invalidateResource = useCallback((resourceName) => {
        assertResourceName(resourceName);

        generationsRef.current[resourceName] += 1;
        controllersRef.current[resourceName]?.abort();
        delete controllersRef.current[resourceName];
        delete inFlightRef.current[resourceName];
        commitResource(resourceName, createResourceState());
    }, [commitResource]);

    const retryResource = useCallback((resourceName) => {
        invalidateResource(resourceName);
        return ensureResource(resourceName);
    }, [ensureResource, invalidateResource]);

    const ensureProductos = useCallback(
        () => ensureResource('productos'),
        [ensureResource]
    );
    const ensureCategorias = useCallback(
        () => ensureResource('categorias'),
        [ensureResource]
    );
    const ensureSubcategorias = useCallback(
        () => ensureResource('subcategorias'),
        [ensureResource]
    );
    const ensureTienda = useCallback(
        () => ensureResource('tienda'),
        [ensureResource]
    );

    const retryProductos = useCallback(
        () => retryResource('productos'),
        [retryResource]
    );
    const retryCategorias = useCallback(
        () => retryResource('categorias'),
        [retryResource]
    );
    const retrySubcategorias = useCallback(
        () => retryResource('subcategorias'),
        [retryResource]
    );
    const retryTienda = useCallback(
        () => retryResource('tienda'),
        [retryResource]
    );

    const value = useMemo(() => ({
        productos: resources.productos.data,
        categorias: resources.categorias.data,
        subcategorias: resources.subcategorias.data,
        tienda: resources.tienda.data,
        tiendaPrincipal: resources.tienda.data[0] || null,
        status: {
            productos: resources.productos.status,
            categorias: resources.categorias.status,
            subcategorias: resources.subcategorias.status,
            tienda: resources.tienda.status,
        },
        errors: {
            productos: resources.productos.error,
            categorias: resources.categorias.error,
            subcategorias: resources.subcategorias.error,
            tienda: resources.tienda.error,
        },
        updatedAt: {
            productos: resources.productos.updatedAt,
            categorias: resources.categorias.updatedAt,
            subcategorias: resources.subcategorias.updatedAt,
            tienda: resources.tienda.updatedAt,
        },
        ensureProductos,
        ensureCategorias,
        ensureSubcategorias,
        ensureTienda,
        retryProductos,
        retryCategorias,
        retrySubcategorias,
        retryTienda,
        invalidateResource,
    }), [
        ensureCategorias,
        ensureProductos,
        ensureSubcategorias,
        ensureTienda,
        invalidateResource,
        resources,
        retryCategorias,
        retryProductos,
        retrySubcategorias,
        retryTienda,
    ]);

    return (
        <CatalogDataContext.Provider value={value}>
            {children}
        </CatalogDataContext.Provider>
    );
}

export function useCatalogData() {
    const context = useContext(CatalogDataContext);

    if (!context) {
        throw new Error('useCatalogData debe usarse dentro de CatalogDataProvider.');
    }

    return context;
}

export function useOptionalCatalogData() {
    return useContext(CatalogDataContext);
}

export default CatalogDataProvider;
